import { useState, useEffect, useRef } from 'react';
import { X, Copy } from 'lucide-react';

const DuplicateEntityModal = ({ isOpen, onClose, onConfirm, originalName }) => {
  const [newName, setNewName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Suggest a name like "originalName_copy" or "originalName_2"
      const suggestedName = `${originalName}_copy`;
      setNewName(suggestedName);
      
      // Focus and select the input after a brief delay
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 100);
    }
  }, [isOpen, originalName]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      return;
    }

    onConfirm(newName.trim());
    setNewName('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Copy className="text-blue-600 dark:text-blue-400" size={20} />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              Duplicate Entity
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Close"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Duplicating entity <strong className="text-gray-900 dark:text-white">"{originalName}"</strong>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
              Fields and tags will be copied. Relationships will NOT be duplicated.
            </p>
            
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Entity Name *
            </label>
            <input
              ref={inputRef}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter name for duplicated entity"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newName.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Copy size={16} />
              Duplicate
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DuplicateEntityModal;
